import { ConfigurableFocusTrapFactory } from "@angular/cdk/a11y";
import { DOCUMENT } from "@angular/common";
import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Optional,
	Output,
	ViewContainerRef,
} from "@angular/core";
import { UntypedFormControl, Validators } from "@angular/forms";
import { resolveTagDisplayColor } from "@app/_helpers/tag-color";
import { createRxValue, fromRxValue, hasChange } from "@app/_helpers/utils";
import { AppService } from "@app/app.service";
import { SatPopoverComponent as SatPopover, SatPopoverAnchoringService } from "@ncstate/sat-popover";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Logger, Tag, TagsQuery, TagType, UserSettingsQuery } from "@timeghost/web-api";
import { pick } from "lodash-es";
import { combineLatest } from "rxjs";
import { distinctUntilChanged, map, startWith } from "rxjs/operators";
import { firstBy } from "thenby";

const TagTypeNames = {
	[TagType.Project]: "tag.project",
	[TagType.Time]: "tag.time",
};
const log = new Logger("TagFilterPopoverComponent");

@UntilDestroy()
@Component({
	selector: "tg-tag-filter-popover",
	templateUrl: "./tag-filter-popover.component.html",
	styleUrls: ["./tag-filter-popover.component.scss"],
	providers: [{ provide: SatPopoverAnchoringService, useExisting: false }],
})
export class TagFilterPopoverComponent extends SatPopover implements OnInit {
	constructor(
		private userSettingsQuery: UserSettingsQuery,
		private tagsQuery: TagsQuery,
		private appService: AppService,
		_focusTrapFactory: ConfigurableFocusTrapFactory,
		_anchoringService: SatPopoverAnchoringService,
		_viewContainerRef: ViewContainerRef,
		@Optional() @Inject(DOCUMENT) _document: any,
		private cdr: ChangeDetectorRef
	) {
		super(
			_focusTrapFactory,
			_anchoringService,
			_viewContainerRef,
			"200ms cubic-bezier(0.25, 0.8, 0.25, 1)",
			_document
		);
	}

	search = new UntypedFormControl("", [Validators.required, Validators.minLength(2)]);
	@Input() tagType?: TagType;
	private _selectedItems = createRxValue<Tag[]>([]);
	private selectedItems$ = this._selectedItems.asObservable();

	get selectedItems() {
		return this._selectedItems.value;
	}

	@Input()
	set selectedItems(val: Tag[]) {
		const prev = this.selectedItems;
		this._selectedItems.value = val;

		if (hasChange(val, prev, (s) => s?.map((d) => d.id))) this.selectedItemsChange.emit(val);
	}

	@Output() selectedItemsChange = new EventEmitter(true);

	readonly items$filtered = fromRxValue(
		combineLatest([
			this.search.valueChanges.pipe(startWith(this.search.value), distinctUntilChanged()),
			this.tagsQuery.selectAll().pipe(startWith(this.tagsQuery.getAll())),
			this.selectedItems$,
		]).pipe(
			map(([q, tags, selected]: [string, Tag[], Tag[]]) => {
				const newItems = (() => {
					if (this.search.invalid) return tags;
					return tags.filter((x) => {
						return x.name.toLowerCase().indexOf(q.toLowerCase()) !== -1;
					});
				})()
					.map((x) => {
						return {
							...x,
							selected: !!selected?.find((u) => u.id === x.id),
							type: TagTypeNames[x.tagType],
						};
					})
					.sort(firstBy("selected", "desc").thenBy("name", "asc"));
				log.debug("tag-filter", newItems);
				return newItems;
			})
		)
	);

	@Output() itemsClose = new EventEmitter(true);

	ngOnInit(): void {
		this.openAnimationStartAtScale = this.closeAnimationEndAtScale = 0.9375;
		this.hasBackdrop = true;
		this.yAlign = "below";
		this.xAlign = "center";
		this.closed.asObservable().subscribe(() => this.search.reset(""));
		this.closed
			.asObservable()
			.pipe(
				untilDestroyed(this),
				map(() => this.selectedItems),
				distinctUntilChanged()
			)
			.subscribe((val) => this.itemsClose.emit(val));
	}

	trackId(index: number, { id }: { id: string }) {
		return id;
	}

	tagDisplayColor(tag: Tag & { color?: string | null }) {
		return resolveTagDisplayColor(tag.color, tag.id, (n) => this.appService.colorScheme.getColorFromScheme(n));
	}

	selectItem(tag: Tag) {
		tag = pick(tag, "id", "name", "tagType") as Tag;
		this._selectedItems.update((s) => {
			if (this.selectedItems.findIndex((x) => x.id === tag.id) !== -1)
				s = this.selectedItems.filter((x) => x.id !== tag.id);
			else s = [...this.selectedItems, tag];
			return s;
		}, true);
	}

	selectAll() {
		this._selectedItems.update(() => {
			return this.items$filtered.value;
		});
	}

	selectNone() {
		this._selectedItems.update(() => {
			return [];
		});
	}

	close() {
		super.close(this.selectedItems);
	}
}
